import { AxiosResponse } from 'axios';
import { getAxiosInstance } from '@/client/get-axios-instance';
import { buildMarketplaceApiPath } from '@/repositories/marketplace-api-paths';
import {
    createGroupMutationRepositoryHandlers,
    GroupCsvDownloadResult,
    GroupExtractResponse,
    GroupMergePayload,
    GroupMergeResponse,
    GroupRenamePayload,
    GroupRenameResponse,
    GroupMutationRepositoryHandlers,
} from '@/repositories/group-mutation-repository-handlers';
import { EndpointOptions } from '@/types/endpoint.options';

type CreateGroupRepositoryCoreArgs = {
    resourcePath: 'brands' | 'products';
    csvFileName: string;
};

const GROUP_CSV_DOWNLOAD_ERROR_CONFIG = {
    brands: {
        code: 'groups_brands.transport.csv_download_failed',
        message: 'Failed to download brand groups CSV',
    },
    products: {
        code: 'groups_products.transport.csv_download_failed',
        message: 'Failed to download products CSV',
    },
} as const;

export type GroupRepositoryCore<
    TListResponse,
    TGroupDetailsResponse,
    TRenameResponse extends GroupRenameResponse,
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends GroupExtractResponse,
    TCsvQuery extends Record<string, unknown>,
> = {
    axios: ReturnType<typeof getAxiosInstance>;
    getList: (options?: EndpointOptions) => Promise<AxiosResponse<TListResponse>>;
    mergeGroups: (data: GroupMergePayload, options?: EndpointOptions) => Promise<AxiosResponse<GroupMergeResponse>>;
    getGroup: (groupId: string, options?: EndpointOptions) => Promise<AxiosResponse<TGroupDetailsResponse>>;
    renameGroup: (
        groupId: string,
        data: GroupRenamePayload,
        options?: EndpointOptions,
    ) => Promise<AxiosResponse<TRenameResponse>>;
    extractFromGroup: (
        groupId: string,
        data: TExtractPayload,
        options?: EndpointOptions,
    ) => Promise<AxiosResponse<TExtractResponse>>;
    downloadCsv: (query: TCsvQuery, options?: EndpointOptions) => Promise<GroupCsvDownloadResult>;
};

export const createGroupRepositoryCore = <
    TListResponse,
    TGroupDetailsResponse,
    TRenameResponse extends GroupRenameResponse,
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends GroupExtractResponse,
    TCsvQuery extends Record<string, unknown>,
>({
    resourcePath,
    csvFileName,
}: CreateGroupRepositoryCoreArgs): GroupRepositoryCore<
    TListResponse,
    TGroupDetailsResponse,
    TRenameResponse,
    TExtractPayload,
    TExtractResponse,
    TCsvQuery
> => {
    const axios = getAxiosInstance(buildMarketplaceApiPath(resourcePath));
    const csvDownloadErrorConfig = GROUP_CSV_DOWNLOAD_ERROR_CONFIG[resourcePath];
    const groupMutationHandlers: GroupMutationRepositoryHandlers<
        TGroupDetailsResponse,
        TRenameResponse,
        TExtractPayload,
        TExtractResponse,
        TCsvQuery
    > = createGroupMutationRepositoryHandlers({
        axios,
        csvFileName,
        csvDownloadFallbackCode: csvDownloadErrorConfig.code,
        csvDownloadFallbackMessage: csvDownloadErrorConfig.message,
    });

    return {
        axios,
        getList(options) {
            return axios.get<TListResponse>('', {
                headers: { Authorization: options?.authToken },
                signal: options?.signal,
            });
        },
        mergeGroups(data, options) {
            return groupMutationHandlers.mergeGroups(data, options);
        },
        getGroup(groupId, options) {
            return groupMutationHandlers.getGroup(groupId, options);
        },
        renameGroup(groupId, data, options) {
            return groupMutationHandlers.renameGroup(groupId, data, options);
        },
        extractFromGroup(groupId, data, options) {
            return groupMutationHandlers.extractFromGroup(groupId, data, options);
        },
        downloadCsv(query, options) {
            return groupMutationHandlers.downloadCsv(query, options);
        },
    };
};
